import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import LoadingSpinner from '../../components/LoadingSpinner';

// Icons
import { Search, Eye, Ban, Megaphone } from 'lucide-react';

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const AdminCampaigns = () => {
  const { currentUser } = useAuth();
  const { showError, handleAsyncOperation } = useFeedback();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [participantCounts, setParticipantCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        setError(null);

        const [campaignsSnap, participantsSnap] = await Promise.all([
          getDocs(collection(db, 'campaigns')),
          getDocs(collection(db, 'campaignParticipants')),
        ]);

        const campaignsData = campaignsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort by createdAt desc
        campaignsData.sort((a, b) => {
          const aTime = a.createdAt?.seconds || (a.createdAt instanceof Date ? a.createdAt.getTime() / 1000 : 0);
          const bTime = b.createdAt?.seconds || (b.createdAt instanceof Date ? b.createdAt.getTime() / 1000 : 0);
          return bTime - aTime;
        });

        setCampaigns(campaignsData);

        // Count participants per campaign
        const counts = {};
        participantsSnap.docs.forEach((doc) => {
          const data = doc.data();
          const campaignId = data.campaignId;
          if (campaignId) {
            counts[campaignId] = (counts[campaignId] || 0) + 1;
          }
        });
        setParticipantCounts(counts);
      } catch (err) {
        console.error('Error fetching campaigns:', err);
        setError('Failed to load campaigns. Please try again.');
        showError('Error', 'Failed to load campaigns.');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [currentUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtered campaigns based on search and status filter
  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesStatus = filterStatus === 'all' || campaign.status === filterStatus;
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      !searchTerm ||
      (campaign.name || '').toLowerCase().includes(searchLower) ||
      (campaign.title || '').toLowerCase().includes(searchLower) ||
      (campaign.brandName || '').toLowerCase().includes(searchLower);
    return matchesStatus && matchesSearch;
  });

  const handleTakeDown = async (campaignId) => {
    if (!window.confirm('Are you sure you want to take down this campaign? It will no longer be visible to users.')) return;

    setActionLoading(campaignId);
    try {
      await handleAsyncOperation(
        async () => {
          await updateDoc(doc(db, 'campaigns', campaignId), { status: 'taken_down' });
        },
        { title: 'Taking down campaign...', message: 'Please wait while we update the campaign status.' },
        { title: 'Campaign Taken Down', message: 'The campaign has been taken down successfully.' },
        { title: 'Error', message: 'Failed to take down campaign. Please try again.' }
      );

      // Update local state
      setCampaigns((prev) =>
        prev.map((c) => (c.id === campaignId ? { ...c, status: 'taken_down' } : c))
      );
    } catch (err) {
      console.error('Error taking down campaign:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Campaign Management</h2>
          <p className="text-gray-600">View and manage all platform campaigns</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-1">Something went wrong</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Campaign Management</h2>
        <p className="text-gray-600">View and manage all platform campaigns</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by campaign or brand name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          {/* Filter by status */}
          <div className="sm:w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="completed">Completed</option>
              <option value="paused">Paused</option>
              <option value="taken_down">Taken Down</option>
            </select>
          </div>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Influencers</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCampaigns.length > 0 ? (
                filteredCampaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {campaign.name || campaign.title || 'Untitled Campaign'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{campaign.brandName || 'Unknown Brand'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                        campaign.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                        campaign.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                        campaign.status === 'paused' ? 'bg-amber-100 text-amber-800' :
                        campaign.status === 'taken_down' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {campaign.status || 'unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(campaign.createdAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{participantCounts[campaign.id] || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => window.open(`/admin/campaigns`, '_blank')}
                          className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {campaign.status !== 'taken_down' && (
                          <button
                            onClick={() => handleTakeDown(campaign.id)}
                            disabled={actionLoading === campaign.id}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                            title="Take Down"
                          >
                            <Ban className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <Megaphone className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No campaigns found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Count */}
      <div className="mt-4 text-sm text-gray-500">
        Showing {filteredCampaigns.length} of {campaigns.length} campaigns
      </div>
    </div>
  );
};

export default AdminCampaigns;
